import React, { FC, useEffect, useState } from "react"

import { ReactComponent as IconDots } from "@common_assets/svg/threeDots.svg"
import {
  Fab,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material"
import { useMutationsResetBalance } from "api/reactQuery/mutations/balancesReset"
import { IPostClassesTransactionsInput } from "api/reactQuery/mutations/classesTransactions.types"
import {
  useMutationBulkDeleteStudents,
  useMutationRemoveStudentsClass,
} from "api/reactQuery/mutations/students"
import { IPostBulkDeleteRemoveStudents } from "api/reactQuery/mutations/students.types"
import { useQueryHelpUpdateClassesStudents } from "api/reactQuery/queries/classesStudents"
import IconWrapper from "components/common/icon/iconWrapper"
import { PopOverStyle, PopRow } from "components/common/poprow"
import SendStoreChargeDialog from "components/module/storeCharge/sendStoreChargeDialog"
import StoreChargeEntryButton from "components/module/storeCharge/StoreChargeEntryButton"
import { useIsBreakpointUp } from "hooks/breakpoint"
import { useDialog } from "hooks/dialog"
import {
  useFilterClassListArray,
  userUpdateBalanceClasslist,
} from "hooks/filter"
import { useClassId } from "hooks/navigation"
import { usePopover } from "hooks/popover"
import { useCanDeleteStudent, useIsSchoolPlan } from "hooks/schoolPlans"
import { useCustomSnackbar } from "hooks/snackbar"
import { useAppDispatch, useAppSelector } from "hooks/store"
import mixpanel from "mixpanel-browser"
import { useTranslation } from "react-i18next"
import { useQueryClient } from "react-query"
import { useParams } from "react-router"
import {
  selectClassList,
  selectClassname,
  selectResetBalanceTaskId,
} from "store/utility/utility.selectors"
import {
  setClassList,
  setIsNextPage,
  setPage,
  setResetBalanceTaskId,
  setStudentsNumber,
} from "store/utility/utility.slice"
import { IObjectStudentDetailed } from "ts/interfaces/Student"
import { TransactionType } from "ts/types/Transaction"
import { getErrorMessage } from "utils/api"
import { setPersistedResetBalanceTaskId } from "utils/resetBalanceTask"

import DialogRemoveStudent from "../../DialogRemoveStudent"
import DialogContent from "../../DialogRemoveStudent/DialogContent"
import TransactionsDialogForm from "../transactionsDialogForm"
import {
  BatchActionsContainer,
  TransactionButton,
} from "./BatchActionButtons.styles"
import BatchInfo from "./batchInfo"
import ScrollableActions from "./scrollableActions"

interface IProps {
  clearSelectedItems: () => void
  file?: string
  isGroupFilterActive?: boolean
  clearExcludedItems: () => void
  studentsNumber: number
  isLoading: boolean
  stateSelectedItems: {
    setSelectedItems: React.Dispatch<
      React.SetStateAction<IObjectStudentDetailed[]>
    >
    selectedItems: IObjectStudentDetailed[]
  }
  stateExcludedItems: {
    setExcludedItems: React.Dispatch<
      React.SetStateAction<IObjectStudentDetailed[]>
    >
    excludedItems: IObjectStudentDetailed[]
  }
  stateIsToggleAll: {
    setIsToggle?: React.Dispatch<React.SetStateAction<boolean>>
    isToggleAll?: boolean
  }
}

const BatchActionButtons: FC<IProps> = ({
  clearSelectedItems,
  file,
  isGroupFilterActive = false,
  clearExcludedItems,
  studentsNumber,
  isLoading,
  stateIsToggleAll,
  stateExcludedItems,
  stateSelectedItems,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { isToggleAll, setIsToggle } = stateIsToggleAll
  const { selectedItems, setSelectedItems } = stateSelectedItems
  const { excludedItems } = stateExcludedItems
  const params = useParams()
  const classId = useClassId()
  const dispatch = useAppDispatch()
  const classList = useAppSelector(selectClassList)
  const { showSnackbar } = useCustomSnackbar()
  const classname = useAppSelector(selectClassname)
  const resetBalanceTaskId = useAppSelector(selectResetBalanceTaskId)
  const isResetBalanceInFlight = !!resetBalanceTaskId
  const [defaultExpandedSections, setDefaultExpandedSections] = useState<
    TransactionType[]
  >([])
  const isTablet = useIsBreakpointUp("tablet")
  const isBetweenTablet = useMediaQuery(
    theme.breakpoints.between("tablet", "desktop"),
  )

  const isSchoolPlan = useIsSchoolPlan()
  const canDeleteStudent = useCanDeleteStudent()

  const [variablesBulkAction, setVariablesBulkAction] =
    useState<IPostClassesTransactionsInput | null>(null)
  const [variables, setVariables] =
    useState<IPostBulkDeleteRemoveStudents | null>(null)
  const queryClient = useQueryClient()
  const [isUpdate, setIsUpdate] = useState(false)
  const [isClickOnButton, setIsClickOnButton] = useState(false)
  const [storeChargeChargedIds, setStoreChargeChargedIds] = useState<
    number[] | null
  >(null)

  const selectedIds = selectedItems.map(({ id }) => id)
  const excludedIds = excludedItems.map(({ id }) => id)
  // Whole-class semantics (`all_selected`/`send_to_whole_class: true` + excluded_ids) are
  // only valid when nothing narrows the visible list — otherwise "select all" must send
  // the concrete filtered ids, or the action silently targets the entire class.
  const isWholeClassSelectAll =
    isToggleAll && !file?.length && !isGroupFilterActive

  const {
    isOpen: isOpenTransactions,
    handleOpen: handleOpenTransactions,
    handleClose: handleCloseTransactions,
  } = useDialog()

  const {
    isOpen: isOpenRemoveStudents,
    handleClose: handleCloseRemoveStudents,
    handleOpen: handleOpenRemoveStudents,
  } = useDialog()

  const {
    isOpen: isOpenDeleteStudents,
    handleClose: handleCloseDeleteStudents,
    handleOpen: handleOpenDeleteStudents,
  } = useDialog()

  const {
    isOpen: isOpenResetBalance,
    handleOpen: handleOpenResetBalance,
    handleClose: handleCloseResetBalance,
  } = useDialog()

  const {
    isOpen: isOpenStoreCharge,
    handleOpen: handleOpenStoreCharge,
    handleClose: handleCloseStoreCharge,
  } = useDialog()

  const storeChargeCount = isWholeClassSelectAll
    ? studentsNumber - excludedItems.length
    : selectedItems.length

  const clearStoreChargeSelection = () => {
    handleCloseStoreCharge()
    clearSelectedItems()
    clearExcludedItems()
    setIsToggle && setIsToggle(false)
  }

  const { data } = useQueryHelpUpdateClassesStudents({
    classId: Number(params.classId),
    page: 0,
    limit: 200,
    options: {
      enabled: isUpdate,
      cacheTime: 0,
      staleTime: 0,
    },
  })

  const { mutate: resetBalances, isLoading: isLoadingResetBalances } =
    useMutationsResetBalance({
      classId: Number(params.classId),
      options: {
        onSuccess: (response) => {
          const taskId = response.data?.task_id

          if (!taskId) {
            showSnackbar({
              title: t("students.somethingWentWrongTryAgain"),
              variant: "error",
            })
            return
          }

          dispatch(setResetBalanceTaskId(taskId))
          setPersistedResetBalanceTaskId(classId, taskId)

          clearSelectedItems()
          clearExcludedItems()
          setIsToggle && setIsToggle(false)
          handleCloseResetBalance()
          handleClose()
        },
        onError: (err) => {
          showSnackbar({
            title: getErrorMessage(err),
            variant: "error",
          })
        },
      },
    })

  const { mutate: deleteStudents, isLoading: isLoadingDeleteStudents } =
    useMutationBulkDeleteStudents({
      class_id: Number(params.classId),
      options: {
        onSuccess: (_, variables) => {
          showSnackbar({
            title: t("students.studentHaveBeenSuccessfullyDeleted"),
          })
          queryClient.invalidateQueries("classesStudents")
          if (isToggleAll) {
            dispatch(setIsNextPage(false))
            dispatch(setPage(0))
          }
          setVariables(variables)
          handleCloseDeleteStudents()
        },
        onError: (err) => {
          showSnackbar({
            title: getErrorMessage(err),
            variant: "error",
          })
        },
      },
    })
  const { mutate: removeStudents, isLoading: isLoadingRemoveStudents } =
    useMutationRemoveStudentsClass({
      class_id: Number(params.classId),
      options: {
        onSuccess: (_, variables) => {
          showSnackbar({
            title: t("students.studentsHaveBeenSuccessfullyRemovedFromClass"),
          })

          queryClient.invalidateQueries("classesCandidates")
          queryClient.invalidateQueries("classesStudents")
          if (isToggleAll) {
            dispatch(setIsNextPage(false))
            dispatch(setPage(0))
          }
          setVariables(variables)
          handleCloseRemoveStudents()
        },
        onError: (err) => {
          showSnackbar({
            title: getErrorMessage(err),
            variant: "error",
          })
        },
      },
    })

  useEffect(() => {
    if (variables !== null) {
      const variables_selected = variables.data.selected_ids
      const { filteredState } = useFilterClassListArray({
        classlist: classList,
        variables,
      })
      dispatch(setClassList(filteredState))

      if (variables.data.all_selected) {
        clearSelectedItems()
        clearExcludedItems()
        setIsToggle && setIsToggle(false)
        dispatch(setStudentsNumber(0))
      }

      if (!variables.data.all_selected) {
        setSelectedItems((prev) =>
          prev.filter((el) => {
            return !variables_selected?.some((variable) => {
              return el.id === variable
            })
          }),
        )

        dispatch(
          setStudentsNumber(
            studentsNumber - Number(variables_selected?.length),
          ),
        )
      }
      handleClose()
    }
  }, [variables])

  useEffect(() => {
    if (data && isClickOnButton) {
      const updatedClassList = userUpdateBalanceClasslist({
        classlist: classList,
        variable: storeChargeChargedIds
          ? { data: { student_ids: storeChargeChargedIds } }
          : variablesBulkAction,
        updatedClasslist: data?.data.objects,
      })

      dispatch(setClassList(updatedClassList))
      setIsUpdate(false)
      setVariablesBulkAction(null)
      setStoreChargeChargedIds(null)
      setIsClickOnButton(false)
    }
  }, [data])

  const { popoverAnchorEl, handleClose, handleClick } =
    usePopover<HTMLButtonElement>()

  if (selectedItems.length === 0) return <BatchInfo />

  return (
    <>
      <BatchActionsContainer isBetweenTablet={isBetweenTablet}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            width: "100%",
            minWidth: 0,
          }}
        >
          <Typography
            variant="subtitle1"
            color="mockup.neutral10"
            sx={{
              minWidth: "max-content",
              flexShrink: 0,
              mr: { mobile: 1, tablet: 2 },
            }}
          >
            {t("students.numberOfStudentsSelected", {
              quantity: isWholeClassSelectAll
                ? studentsNumber - excludedItems.length
                : isLoading
                  ? "-"
                  : selectedItems.length,
            })}
          </Typography>
          <ScrollableActions>
            <Stack
              direction="row"
              gap={{ mobile: "0px", tablet: "8px" }}
              sx={{
                width: "max-content",
                mx: { mobile: "-8px", tablet: "0px" },
                py: { mobile: "4px", tablet: "0px" },
              }}
              onClick={() => setIsClickOnButton(true)}
            >
            <TransactionButton
              scheme="bonus"
              variant={isTablet ? "outlined" : "text"}
              onClick={() => {
                mixpanel.track("send_transaction", {
                  event_name: "send_bonuses",
                })
                setDefaultExpandedSections(["bonus"])
                handleOpenTransactions()
              }}
            >
              {t("students.sendBonuses")}
            </TransactionButton>
            <TransactionButton
              scheme="fine"
              variant={isTablet ? "outlined" : "text"}
              onClick={() => {
                mixpanel.track("send_transaction", {
                  event_name: "send_fines",
                })
                setDefaultExpandedSections(["fine"])
                handleOpenTransactions()
              }}
            >
              {t("students.sendFines")}
            </TransactionButton>
            <TransactionButton
              scheme="salary"
              variant={isTablet ? "outlined" : "text"}
              onClick={() => {
                mixpanel.track("send_transaction", {
                  event_name: "send_paychecks",
                })
                setDefaultExpandedSections(["salary"])
                handleOpenTransactions()
              }}
            >
              {t("students.sendPaychecks")}
            </TransactionButton>
            <TransactionButton
              scheme="expense"
              variant={isTablet ? "outlined" : "text"}
              onClick={() => {
                mixpanel.track("send_transaction", {
                  event_name: "send_expenses",
                })
                setDefaultExpandedSections(["expense"])
                handleOpenTransactions()
              }}
            >
              {t("students.sendExpenses")}
            </TransactionButton>
            <StoreChargeEntryButton
              isTablet={isTablet}
              onClick={handleOpenStoreCharge}
            />
            </Stack>
          </ScrollableActions>
          <Stack
            direction="row"
            sx={{ flexShrink: 0, ml: 1 }}
            onClick={() => setIsClickOnButton(true)}
          >
            <Fab
              size={isTablet ? "large" : "small"}
              onClick={handleClick}
            >
              <IconWrapper>
                <IconDots />
              </IconWrapper>
            </Fab>
            <PopOverStyle
              disableScrollLock
              open={!!popoverAnchorEl}
              onClose={handleClose}
              anchorEl={popoverAnchorEl}
              marginThreshold={40}
              anchorOrigin={{
                vertical: isTablet ? 50 : 40,
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <ul style={{ listStyleType: "none" }}>
                <li>
                  <PopRow
                    onClick={() => {
                      if (isResetBalanceInFlight) return
                      handleOpenResetBalance()
                    }}
                    sx={{ opacity: isResetBalanceInFlight ? 0.3 : 1 }}
                  >
                    {t("students.resetBalance")}
                  </PopRow>
                </li>
                {isSchoolPlan && (
                  <li>
                    <PopRow onClick={handleOpenRemoveStudents}>
                      {t("students.removeFromClass")}
                    </PopRow>
                  </li>
                )}
                {canDeleteStudent && (
                  <li>
                    <PopRow onClick={handleOpenDeleteStudents}>
                      {t("students.deleteStudent")}
                    </PopRow>
                  </li>
                )}
              </ul>
            </PopOverStyle>
          </Stack>
        </Stack>
      </BatchActionsContainer>
      <TransactionsDialogForm
        setVariables={setVariablesBulkAction}
        setIsToggle={setIsToggle}
        isOpen={isOpenTransactions}
        file={file}
        isGroupFilterActive={isGroupFilterActive}
        isToggleAll={isToggleAll}
        handleClose={handleCloseTransactions}
        defaultExpandedSections={defaultExpandedSections}
        excludedItems={excludedItems}
        studentsIds={selectedItems.map((student) => student.id)}
        onSuccess={() => {
          handleCloseTransactions()
          clearSelectedItems()
          clearExcludedItems && clearExcludedItems()
          setIsUpdate(true)
        }}
      />
      {isOpenStoreCharge && (
        <SendStoreChargeDialog
          isOpen
          entry="picker"
          students={selectedItems}
          isWholeClass={isWholeClassSelectAll}
          excludedStudentIds={excludedIds}
          excludedStudents={excludedItems}
          wholeClassCount={storeChargeCount}
          handleClose={handleCloseStoreCharge}
          onCharged={(chargedStudentIds) => {
            clearStoreChargeSelection()
            if (chargedStudentIds.length > 0) {
              setStoreChargeChargedIds(chargedStudentIds)
              setIsClickOnButton(true)
              setIsUpdate(true)
            }
          }}
        />
      )}
      <DialogRemoveStudent
        titleText={t("students.areYouSureYouWantToResetBalancesForClass", {
          classname,
        })}
        isOpen={isOpenResetBalance}
        onClose={(_, reason) => {
          if (
            (isLoadingResetBalances || isResetBalanceInFlight) &&
            (reason === "backdropClick" || reason === "escapeKeyDown")
          ) {
            return
          }
          handleCloseResetBalance()
        }}
        onActionButtonClick={() => {
          if (isResetBalanceInFlight) return
          if (isWholeClassSelectAll) {
            resetBalances({
              all_selected: isToggleAll,
              excluded_ids: excludedIds,
            })
          } else {
            resetBalances({ selected_ids: selectedIds })
          }
        }}
        actionAcceptText={t("students.resetBalance")}
        disableEscapeKeyDown={isLoadingResetBalances || isResetBalanceInFlight}
        actionAcceptButtonProps={{
          disabled: isLoadingResetBalances || isResetBalanceInFlight,
        }}
        isLoading={isLoadingResetBalances}
      >
        <DialogContent
          isToggleAll={isWholeClassSelectAll}
          excludedItems={excludedItems}
          selectedItems={selectedItems}
          actionText="ResetBalance"
        />
      </DialogRemoveStudent>
      <DialogRemoveStudent
        titleText={t(
          "students.areYouSureYouWantToRemoveTheseStudentsFromTheClass",
          {
            classname,
          },
        )}
        isOpen={isOpenRemoveStudents}
        onClose={(_, reason) => {
          if (
            isLoadingRemoveStudents &&
            (reason === "backdropClick" || reason === "escapeKeyDown")
          ) {
            return
          }
          handleCloseRemoveStudents()
        }}
        actionAcceptText={t("students.removeFromClass")}
        actionAcceptButtonProps={{ disabled: isLoadingRemoveStudents }}
        isLoading={isLoadingRemoveStudents}
        disableEscapeKeyDown={isLoadingRemoveStudents}
        onActionButtonClick={() => {
          if (isWholeClassSelectAll) {
            removeStudents({
              data: {
                all_selected: isToggleAll,
                excluded_ids: excludedIds,
              },
            })
          } else {
            removeStudents({
              data: {
                selected_ids: selectedIds,
              },
            })
          }
        }}
      >
        <DialogContent
          actionText="Remove"
          isToggleAll={isWholeClassSelectAll}
          excludedItems={excludedItems}
          selectedItems={selectedItems}
        />
      </DialogRemoveStudent>
      <DialogRemoveStudent
        titleText={t("students.areYouSureYouWantToDeleteStudents")}
        isOpen={isOpenDeleteStudents}
        onClose={(_, reason) => {
          if (
            isLoadingDeleteStudents &&
            (reason === "backdropClick" || reason === "escapeKeyDown")
          ) {
            return
          }
          handleCloseDeleteStudents()
        }}
        requireDeleteConfirmation
        actionAcceptButtonProps={{
          disabled: isLoadingDeleteStudents,
        }}
        isLoading={isLoadingDeleteStudents}
        disableEscapeKeyDown={isLoadingDeleteStudents}
        onActionButtonClick={() => {
          if (isWholeClassSelectAll) {
            deleteStudents({
              data: {
                all_selected: isToggleAll,
                excluded_ids: excludedIds,
              },
            })
          } else {
            deleteStudents({
              data: {
                selected_ids: selectedIds,
              },
            })
          }
        }}
        actionAcceptText={t("students.delete")}
      >
        <DialogContent
          isToggleAll={isWholeClassSelectAll}
          excludedItems={excludedItems}
          selectedItems={selectedItems}
          actionText="Delete"
          isCaution
        />
      </DialogRemoveStudent>
    </>
  )
}

export default BatchActionButtons
